import JSZip from 'jszip';
import { getFolderTree, presignDownload } from '../api/backupApi';
import { decryptObjectBytes, unwrapKey } from '../crypto/backupCrypto';

// Distinguishable from a plain network/decrypt failure so the caller can
// show a specific "too many files" message rather than a generic one — see
// celepus-api's ObjectsController::MAX_ZIP_FILES for where the cap lives.
class FolderTooLargeError extends Error {}

interface DownloadFolderZipOptions {
  onProgress?: (completed: number, total: number) => void;
}

// Downloads every file recursively under `prefix`, decrypts each one
// client-side — same presign/unwrap/decrypt sequence as opening a single
// file (see MainPage's openFile) — and assembles them into one zip,
// preserving the folder's own relative structure. The server never sees
// plaintext (uploads are end-to-end encrypted), so it can't build the zip
// itself; this is why the whole thing has to happen in the browser.
const downloadFolderZip = async (
  deviceUuid: string,
  prefix: string,
  masterKey: CryptoKey,
  zipFileName: string,
  options: DownloadFolderZipOptions = {}
): Promise<void> => {
  const tree = await getFolderTree(deviceUuid, prefix);
  if (tree.truncated) {
    throw new FolderTooLargeError();
  }

  const zip = new JSZip();

  for (let index = 0; index < tree.files.length; index += 1) {
    const file = tree.files[index];
    const fullPath = `${prefix}${file.path}`;
    const { url, encryption } = await presignDownload(deviceUuid, fullPath);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`download failed: HTTP ${response.status} (${fullPath})`);
    }
    const ciphertext = await response.arrayBuffer();

    const dek = await unwrapKey(
      { wrapped: encryption.wrapped_dek, nonce: encryption.dek_nonce },
      masterKey
    );
    const plaintext = await decryptObjectBytes(
      ciphertext,
      dek,
      encryption.chunk_nonce_prefix,
      parseInt(encryption.chunk_size, 10)
    );
    zip.file(file.path, plaintext);

    options.onProgress?.(index + 1, tree.files.length);
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = zipFileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Give the download time to actually start before freeing the blob.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
};

export {
  downloadFolderZip,
  FolderTooLargeError,
};
